import {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useToast } from "native-base";

import Toast from "@/components/atoms/Toast";
import { useCartController } from "@/contexts/CartControllerContext";
import { PaginatedResult } from "@/interfaces/common/PaginatedResult";
import { Order, OrderItem } from "@/interfaces/domain/Order";
import { RootNavigation } from "@/navigation/Root";
import { getClientOrders, getOrderById } from "@/services/api/order";
import { logger } from "@/services/logger";

import { useAppNavigation } from "../application/navigation";
import { useAppSelector } from "../application/store";

export const ORDER_BY_ID_QUERY_KEY = (orderId: string) => `/order-by-id/${orderId}`;

export const useOrderById = (id: string, options?: UseQueryOptions<Order>) => {
  return useQuery<Order>({
    queryKey: [ORDER_BY_ID_QUERY_KEY(id)],
    queryFn: () => getOrderById(id),
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
};

export const useClientOrders = (options?: UseInfiniteQueryOptions<PaginatedResult<Order>>) => {
  const { user } = useAppSelector((store) => store.auth);
  const clientId = user?.cliente?._id;
  const { data, ...rest } = useInfiniteQuery<PaginatedResult<Order>>({
    queryKey: [`/orders/${clientId}`],
    queryFn: ({ pageParam = 1 }) => getClientOrders(clientId, pageParam),
    getNextPageParam: (lastPage) => lastPage.page + 1,
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
  return {
    data: data?.pages.map((page) => page.docs).flat(),
    ...rest,
  };
};

export const useRepeatOrder = () => {
  const { addItems } = useCartController();
  const toast = useToast();
  const { navigate } = useAppNavigation<RootNavigation>();

  const repeatOrder = (order: Order) => {
    if (!order) return;
    const items = order.itens.map<{ item: OrderItem; quantity?: number }>((item) => ({
      item,
      quantity: item.quantidade,
    }));
    addItems(items, () => {
      toast.show({
        render: () => <Toast variant="success" message="Itens adicionados ao carrinho" />,
      });
      navigate("cartStack", { screen: "cart" });
    });
  };

  return {
    repeatOrder,
  };
};
